import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Fragment, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';
import { api, type Page } from '../lib/api';
import { ErrorNotice } from '../ui/feedback';
import { PageHead, Pager, Tabs, useKeysetPage } from '../ui/table';
import { Time } from '../ui/values';
import { ACTIVITY_LABELS, ActivityTarget, activitySummary, FilterSearch, useUrlFilter } from './common';
import { Level } from './OverviewPage';
import { paths } from './paths';
import type { ActivityEntry, AuditEntry, SystemEvent } from './types';

/**
 * Protokolle (design.md §6): eine Tabelle, zwei Quellen (Reiter). Filter Level, Kategorie, Zeitraum, Akteur.
 * Zeile aufklappbar für `details` (formatiertes JSON in Mono).
 */

type Source = 'events' | 'audit' | 'activity';

/** `YYYY-MM-DD` aus dem Datumsfeld ⇒ ISO-Zeitpunkt (Tagesbeginn bzw. Beginn des Folgetags für „bis“). */
const dayStart = (d: string) => (d ? new Date(`${d}T00:00:00`).toISOString() : undefined);
const dayEnd = (d: string) => {
  if (!d) return undefined;
  const t = new Date(`${d}T00:00:00`);
  t.setDate(t.getDate() + 1);
  return t.toISOString();
};

export function LogsPage() {
  const [params, setParams] = useSearchParams();
  const raw = params.get('source');
  const source: Source = raw === 'audit' || raw === 'activity' ? raw : 'events';
  const switchTo = (s: Source) => setParams(s === 'events' ? {} : { source: s });

  return (
    <div className="stack">
      <PageHead
        title="Protokolle"
        context="System-Ereignisse und Spieler-Aktivität der letzten 90 Tage, Admin-Aktionen des letzten Jahres"
      />
      <Tabs
        active={source}
        onChange={switchTo}
        tabs={[
          { key: 'events', label: 'System-Ereignisse' },
          { key: 'activity', label: 'Spieler-Aktivität' },
          { key: 'audit', label: 'Audit-Log' },
        ]}
      />
      {source === 'events' ? <EventsTable /> : source === 'activity' ? <ActivityTable /> : <AuditTable />}
    </div>
  );
}

function DateFilters() {
  const [from, setFrom] = useUrlFilter('from');
  const [to, setTo] = useUrlFilter('to');
  return (
    <>
      <input type="date" aria-label="von" value={from} onChange={(e) => setFrom(e.target.value)} />
      <input type="date" aria-label="bis" value={to} onChange={(e) => setTo(e.target.value)} />
    </>
  );
}

function EventsTable() {
  const page = useKeysetPage();
  const [level, setLevel] = useUrlFilter('level');
  const [category, setCategory] = useUrlFilter('category');
  const [userId, setUserId] = useUrlFilter('userId');
  const [params] = useSearchParams();
  const query = {
    level,
    category,
    userId,
    from: dayStart(params.get('from') ?? ''),
    to: dayEnd(params.get('to') ?? ''),
    cursor: page.cursor,
    limit: page.limit,
  };
  const list = useQuery({
    queryKey: ['admin', 'events', query],
    queryFn: () => api.get<Page<SystemEvent>>('/admin/events', query),
    placeholderData: keepPreviousData,
  });

  return (
    <>
      <div className="filters">
        <select aria-label="Level" value={level} onChange={(e) => setLevel(e.target.value)}>
          <option value="">Alle Level</option>
          <option value="info">INFO</option>
          <option value="warn">WARN</option>
          <option value="error">ERROR</option>
        </select>
        <select aria-label="Kategorie" value={category} onChange={(e) => setCategory(e.target.value)}>
          <option value="">Alle Kategorien</option>
          {['api', 'sync', 'auth', 'mojang', 'discord', 'microsoft', 'jobs', 'admin'].map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <DateFilters />
        {userId && (
          <span className="chip">
            Spieler gefiltert
            <button type="button" aria-label="Spieler-Filter entfernen" onClick={() => setUserId('')}>
              ×
            </button>
          </span>
        )}
      </div>
      {list.isError && <ErrorNotice error={list.error} onRetry={() => list.refetch()} />}
      <LogTable
        label="System-Ereignisse"
        rows={list.data?.items}
        loading={list.isPending}
        headers={['Level', 'Kategorie', 'Meldung', 'Spieler', 'Zeit']}
        empty="Keine Ereignisse für diese Filter."
        row={(e) => ({
          id: e.id,
          details: e.details,
          cells: [
            <Level level={e.level} />,
            <span className="mono">{e.category}</span>,
            e.message,
            e.userId ? <Link to={paths.player(e.userId)}>Spieler</Link> : <span className="muted">–</span>,
            <Time iso={e.createdAt} />,
          ],
        })}
      />
      <Pager page={page} count={list.data?.items.length ?? 0} nextCursor={list.data?.nextCursor} />
    </>
  );
}

function ActivityTable() {
  const page = useKeysetPage();
  const [action, setAction] = useUrlFilter('action');
  const [userId, setUserId] = useUrlFilter('userId');
  const [params] = useSearchParams();
  const query = {
    action,
    userId,
    from: dayStart(params.get('from') ?? ''),
    to: dayEnd(params.get('to') ?? ''),
    cursor: page.cursor,
    limit: page.limit,
  };
  const list = useQuery({
    queryKey: ['admin', 'activity', 'page', query],
    queryFn: () => api.get<Page<ActivityEntry>>('/admin/activity', query),
    placeholderData: keepPreviousData,
  });

  return (
    <>
      <div className="filters">
        <select aria-label="Aktion" value={action} onChange={(e) => setAction(e.target.value)}>
          <option value="">Alle Aktionen</option>
          <option value="auth">Anmeldung (Mod)</option>
          <option value="web">Anmeldung (Browser)</option>
          <option value="chest">Kisten</option>
          <option value="team">Teams</option>
          {Object.entries(ACTIVITY_LABELS).map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </select>
        <DateFilters />
        {userId && (
          <span className="chip">
            Spieler gefiltert
            <button type="button" aria-label="Spieler-Filter entfernen" onClick={() => setUserId('')}>
              ×
            </button>
          </span>
        )}
      </div>
      {list.isError && <ErrorNotice error={list.error} onRetry={() => list.refetch()} />}
      <LogTable
        label="Spieler-Aktivität"
        rows={list.data?.items}
        loading={list.isPending}
        headers={['Aktion', 'Details', 'Spieler', 'Ziel', 'Zeit']}
        empty="Keine Aktivität für diese Filter."
        row={(a) => ({
          id: a.id,
          details: a.details,
          cells: [
            ACTIVITY_LABELS[a.action] ?? <span className="mono">{a.action}</span>,
            activitySummary(a) || <span className="muted">–</span>,
            <Link to={`${paths.logs}?source=activity&userId=${a.user.id}`}>{a.user.name ?? 'Spieler'}</Link>,
            <ActivityTarget a={a} />,
            <Time iso={a.createdAt} />,
          ],
        })}
      />
      <Pager page={page} count={list.data?.items.length ?? 0} nextCursor={list.data?.nextCursor} />
    </>
  );
}

function AuditTable() {
  const page = useKeysetPage();
  const [action, setAction] = useUrlFilter('action');
  const [actorId, setActorId] = useUrlFilter('actorId');
  const [params] = useSearchParams();
  const query = {
    action,
    actorId,
    from: dayStart(params.get('from') ?? ''),
    to: dayEnd(params.get('to') ?? ''),
    cursor: page.cursor,
    limit: page.limit,
  };
  const list = useQuery({
    queryKey: ['admin', 'audit', 'page', query],
    queryFn: () => api.get<Page<AuditEntry>>('/admin/audit-log', query),
    placeholderData: keepPreviousData,
  });

  return (
    <>
      <div className="filters">
        <FilterSearch label="Aktion, z. B. admin.user" value={action} onChange={setAction} />
        <DateFilters />
        {actorId && (
          <span className="chip">
            Akteur gefiltert
            <button type="button" aria-label="Akteur-Filter entfernen" onClick={() => setActorId('')}>
              ×
            </button>
          </span>
        )}
      </div>
      {list.isError && <ErrorNotice error={list.error} onRetry={() => list.refetch()} />}
      <LogTable
        label="Audit-Log"
        rows={list.data?.items}
        loading={list.isPending}
        headers={['Level', 'Aktion', 'Grund', 'Akteur', 'Zeit']}
        empty="Keine Admin-Aktionen für diese Filter."
        row={(a) => ({
          id: a.id,
          details: a.details,
          cells: [
            <Level level="admin" />,
            <span className="mono">{a.action}</span>,
            typeof a.details.reason === 'string' ? a.details.reason : <span className="muted">–</span>,
            a.actor ? (
              <Link to={`${paths.logs}?source=audit&actorId=${a.actor.id}`}>{a.actor.name ?? 'Admin'}</Link>
            ) : (
              <span className="muted">System</span>
            ),
            <Time iso={a.createdAt} />,
          ],
        })}
      />
      <Pager page={page} count={list.data?.items.length ?? 0} nextCursor={list.data?.nextCursor} />
    </>
  );
}

/** Tabelle mit aufklappbaren Zeilen: Der Pfeil in der ersten Spalte zeigt `details`. */
function LogTable<T>({
  label,
  rows,
  loading,
  headers,
  empty,
  row,
}: {
  label: string;
  rows: T[] | undefined;
  loading: boolean;
  headers: string[];
  empty: string;
  row: (r: T) => { id: string; details: Record<string, unknown>; cells: ReactNode[] };
}) {
  const [open, setOpen] = useState<Set<string>>(new Set());
  const toggle = (id: string) =>
    setOpen((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  return (
    <div className="table-wrap">
      <table aria-label={label} aria-busy={loading || undefined}>
        <thead>
          <tr>
            <th scope="col">
              <span className="sr-only">Details</span>
            </th>
            {headers.map((h) => (
              <th key={h} scope="col">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {!loading && rows?.length === 0 && (
            <tr className="empty">
              <td colSpan={headers.length + 1}>{empty}</td>
            </tr>
          )}
          {rows?.map((r) => {
            const { id, details, cells } = row(r);
            const hasDetails = Object.keys(details).length > 0;
            const isOpen = open.has(id);
            return (
              <Fragment key={id}>
                <tr>
                  <td className="expander">
                    {hasDetails && (
                      <button type="button" aria-expanded={isOpen} aria-label="Details anzeigen" onClick={() => toggle(id)}>
                        {isOpen ? <ChevronDown aria-hidden /> : <ChevronRight aria-hidden />}
                      </button>
                    )}
                  </td>
                  {cells.map((c, i) => (
                    <td key={i}>{c}</td>
                  ))}
                </tr>
                {isOpen && (
                  <tr className="details-row">
                    <td />
                    <td colSpan={headers.length}>
                      <pre className="mono">{JSON.stringify(details, null, 2)}</pre>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
